<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import PortalThreadAvatar from 'dashboard/routes/dashboard/conversation/PortalThreadAvatar.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { computed } from 'vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useMapGetter } from 'dashboard/composables/store';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';

const { updateUISettings } = useUISettings();

const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const showCopilotTab = computed(() =>
  isFeatureEnabledonAccount.value(currentAccountId.value, FEATURE_FLAGS.CAPTAIN)
);

const { uiSettings } = useUISettings();
const currentChat = useMapGetter('getSelectedChat');
const isContactSidebarOpen = computed(
  () => uiSettings.value.is_contact_sidebar_open
);
const isCopilotPanelOpen = computed(
  () => uiSettings.value.is_copilot_panel_open
);

const portalPeople = computed(() => {
  const conversation = currentChat.value || {};
  const attributes = conversation.additional_attributes || {};
  const custom = conversation.custom_attributes || {};
  if (attributes.tnm_source !== 'firestore') return [];
  const raw =
    custom.tnm_portal_recipient_roster ||
    attributes.tnm_portal_participants ||
    [];
  const selected = new Set(
    Array.isArray(custom.tnm_portal_recipient_emails)
      ? custom.tnm_portal_recipient_emails.map(email =>
          String(email || '').trim().toLowerCase()
        )
      : []
  );
  const seen = new Set();
  const people = (Array.isArray(raw) ? raw : [])
    .map(person => ({
      email: String(person?.email || '').trim().toLowerCase(),
      name: String(person?.name || person?.email || '').trim(),
    }))
    .filter(
      person => person.email && !seen.has(person.email) && !!seen.add(person.email)
    );
  return selected.size
    ? people.filter(person => selected.has(person.email))
    : people;
});

const toggleConversationSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: !isContactSidebarOpen.value,
    is_copilot_panel_open: false,
  });
};

const handleConversationSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: true,
    is_copilot_panel_open: false,
  });
};

const handleCopilotSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: true,
  });
};

const keyboardEvents = {
  'Alt+KeyO': {
    action: toggleConversationSidebarToggle,
  },
};
useKeyboardEvents(keyboardEvents);
</script>

<template>
  <ButtonGroup
    class="flex flex-col justify-center items-center absolute top-36 xl:top-24 ltr:right-2 rtl:left-2 bg-n-solid-2/90 backdrop-blur-lg border border-n-weak/50 rounded-full gap-1.5 p-1.5 shadow-sm transition-shadow duration-200 hover:shadow !z-20"
  >
    <button
      v-if="portalPeople.length"
      v-tooltip.top="'People on this thread'"
      type="button"
      class="relative flex items-center justify-center min-w-8 h-8 px-1.5 gap-1 transition-all duration-[250ms] rounded-full active:scale-95"
      :class="{ 'bg-n-alpha-2 shadow-sm': isContactSidebarOpen }"
      @click="handleConversationSidebarToggle"
    >
      <span class="i-ph-users-bold flex-shrink-0 text-n-slate-11 size-3" />
      <span class="relative flex items-center -space-x-2 rtl:space-x-reverse">
        <PortalThreadAvatar
          v-for="person in portalPeople.slice(0, 3)"
          :key="person.email"
          :person="person"
          :size="18"
        />
      </span>
      <span
        v-if="portalPeople.length > 3"
        class="absolute -bottom-0.5 -right-0.5 px-1 text-[9px] font-medium rounded-full bg-n-brand text-white"
      >
        +{{ portalPeople.length - 3 }}
      </span>
    </button>
    <Button
      v-else
      v-tooltip.top="$t('CONVERSATION.SIDEBAR.CONTACT')"
      ghost slate sm
      class="!rounded-full transition-all duration-[250ms] ease-out active:!scale-95 active:!brightness-105 active:duration-75"
      :class="{ 'bg-n-alpha-2 active:shadow-sm': isContactSidebarOpen }"
      icon="i-ph-user-bold"
      @click="handleConversationSidebarToggle"
    />
    <Button
      v-if="showCopilotTab"
      v-tooltip.bottom="$t('CONVERSATION.SIDEBAR.COPILOT')"
      ghost
      slate
      sm
      class="!rounded-full transition-all duration-[250ms] ease-out active:!scale-95 active:duration-75"
      :class="{
        'bg-n-alpha-2 !text-n-iris-9 active:!brightness-105 active:shadow-sm':
          isCopilotPanelOpen,
      }"
      icon="i-woot-captain"
      @click="handleCopilotSidebarToggle"
    />
  </ButtonGroup>
</template>
